/**
 * Modal para emitir NFC-e ou NF-e a partir de venda Cupom (relação de vendas).
 * Não altera estoque — apenas transmite documento fiscal.
 */

import { useState, useEffect, useCallback } from 'react'
import { FileText, AlertCircle, CheckCircle, Loader2 } from 'lucide-react'
import ModalERP from '../../ModalERP'
import ClienteSearchAutocomplete from '../ClienteSearchAutocomplete'
import CadastroRapidoClienteModal from '../CadastroRapidoClienteModal'
import NFePreviewModal from '../../NFe/NFePreviewModal'
import { customerService, saleService } from '../../../../services/erpApi'
import { buildNfePaymentsFromSaleRecords } from '../../../../utils/nfePaymentsFromSale'
import { validateCPF, formatCPF } from '../../../../utils/cpfUtils'
import './EmitirNotaFiscalModal.css'

const DOC_TIPO = {
  NFCE: 'nfce',
  NFE: 'nfe'
}

function formatCurrency(value) {
  const num = parseFloat(value) || 0
  return num.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
}

function EmitirNotaFiscalModal({ isOpen, onClose, venda, onSuccess }) {
  const [docTipo, setDocTipo] = useState(DOC_TIPO.NFCE)
  const [cpf, setCpf] = useState('')
  const [cpfError, setCpfError] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)

  const [saleDetail, setSaleDetail] = useState(null)
  const [loadingSale, setLoadingSale] = useState(false)

  const [selectedCustomer, setSelectedCustomer] = useState(null)
  const [quickCreateOpen, setQuickCreateOpen] = useState(false)
  const [previewOpen, setPreviewOpen] = useState(false)

  const resetState = useCallback(() => {
    setDocTipo(DOC_TIPO.NFCE)
    setCpf('')
    setCpfError('')
    setLoading(false)
    setError(null)
    setSuccess(null)
    setSaleDetail(null)
    setSelectedCustomer(null)
    setQuickCreateOpen(false)
    setPreviewOpen(false)
  }, [])

  useEffect(() => {
    if (!isOpen) {
      resetState()
      return
    }

    const loadSale = async () => {
      if (!venda?.id) return
      setLoadingSale(true)
      try {
        const res = await saleService.getById(venda.id)
        if (res.success) {
          setSaleDetail(res.data)
          const customerId = res.data.customer_id || res.data.customerId
          if (customerId) {
            const custRes = await customerService.getById(customerId)
            if (custRes.success && custRes.data) {
              setSelectedCustomer(custRes.data)
            }
          }
        }
      } catch (err) {
        console.warn('Erro ao carregar venda:', err)
      } finally {
        setLoadingSale(false)
      }
    }

    loadSale()
  }, [isOpen, venda?.id, resetState])

  const handleClose = () => {
    if (loading) return
    onClose()
  }

  const resolvePayments = () => {
    const payments = saleDetail?.payments
    if (payments?.length) {
      return buildNfePaymentsFromSaleRecords(payments, saleDetail?.account_receivables || [])
    }
    return []
  }

  const validateNfePrerequisites = () => {
    if (!selectedCustomer?.id) {
      setError('Selecione um cliente com CPF/CNPJ para emitir NF-e')
      return false
    }
    if (!selectedCustomer.cpf_cnpj) {
      setError('Cliente sem CPF/CNPJ cadastrado. Atualize o cadastro antes de emitir.')
      return false
    }
    const payments = resolvePayments()
    if (!payments.length) {
      setError('Nenhum pagamento encontrado para esta venda.')
      return false
    }
    setError(null)
    return true
  }

  const handleOpenPreview = () => {
    if (!validateNfePrerequisites()) return
    setPreviewOpen(true)
  }

  const handleEmitNfce = async () => {
    setCpfError('')
    setError(null)
    setSuccess(null)

    if (!cpf || cpf.replace(/\D/g, '').length === 0) {
      setCpfError('CPF é obrigatório para emissão de NFC-e')
      return
    }

    const cpfNumbers = cpf.replace(/\D/g, '')
    const cpfValidation = validateCPF(cpfNumbers)
    if (cpfValidation) {
      setCpfError(cpfValidation)
      return
    }

    setLoading(true)
    try {
      const response = await saleService.emitirDocumentoFiscal(venda.id, {
        tipo: 'nfce',
        cpf: cpfNumbers,
        emissionSource: 'relacao_vendas',
        skipStock: true
      })

      const data = response.data || {}
      if (response.success && (data.status === 'autorizado' || data.nota_fiscal?.chave_nfe)) {
        setSuccess({
          tipo: 'nfce',
          message: 'NFC-e transmitida com sucesso!',
          urlPdf: `/api/v1/fiscal/nfce/${venda.id}/danfe-proxy`
        })
        onSuccess?.({ tipo: 'nfce', venda: data.venda || venda })
      } else if (response.success && data.status === 'processando') {
        setSuccess({
          tipo: 'nfce',
          message: data.message || 'NFC-e em processamento na SEFAZ.',
          processando: true
        })
        onSuccess?.({ tipo: 'nfce', processando: true })
      } else {
        setError(response.message || 'Erro ao emitir NFC-e')
      }
    } catch (err) {
      const errorData = err.response?.data
      if (errorData?.error === 'DUPLICIDADE_NOTA') {
        setError('Esta venda já possui uma NFC-e emitida')
      } else {
        setError(errorData?.message || err.message || 'Erro ao emitir NFC-e')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleEmitNfe = async () => {
    if (!validateNfePrerequisites()) return

    setError(null)
    setSuccess(null)
    setLoading(true)

    const payments = resolvePayments()

    try {
      const response = await saleService.emitirDocumentoFiscal(venda.id, {
        tipo: 'nfe',
        customerId: selectedCustomer.id,
        payments,
        emissionSource: 'relacao_vendas',
        skipStock: true
      })

      const data = response.data || {}
      const nota = data.nota_fiscal
      const nfeRecord = data.nfe
      const temChave = Boolean(nota?.chave_nfe || nfeRecord?.access_key)
      const temNfeId = Boolean(nfeRecord?.id)

      if (response.success && (temChave || temNfeId || data.status === 'autorizado')) {
        setPreviewOpen(false)
        setSuccess({
          tipo: 'nfe',
          message: 'NF-e transmitida com sucesso!',
          urlPdf: nota?.url_pdf || nfeRecord?.url_pdf
        })
        if (nota?.url_pdf) {
          window.open(nota.url_pdf, '_blank')
        }
        onSuccess?.({ tipo: 'nfe', venda: data.venda || venda, nfe: nfeRecord })
      } else if (response.success && data.status === 'processando') {
        setPreviewOpen(false)
        setSuccess({
          tipo: 'nfe',
          message:
            data.message ||
            'NF-e enviada à SEFAZ. Consulte o status pelo menu da venda.',
          processando: true
        })
        onSuccess?.({ tipo: 'nfe', processando: true, nfe: nfeRecord })
      } else {
        setError(response.message || 'Erro ao emitir NF-e')
      }
    } catch (err) {
      const errorData = err.response?.data
      if (errorData?.error === 'DUPLICIDADE_NOTA' || err.response?.status === 409) {
        setError(errorData?.message || 'Já existe NF-e para esta venda.')
      } else {
        setError(
          errorData?.message || err.message || 'Erro ao emitir NF-e. Verifique cliente e produtos.'
        )
      }
    } finally {
      setLoading(false)
    }
  }

  const totalAmount =
    saleDetail?.final_amount ??
    saleDetail?.total_amount ??
    venda?.final_amount ??
    venda?.total_amount ??
    0

  const saleNumber = venda?.number || saleDetail?.number || venda?.id
  const nfePayments = resolvePayments()

  return (
    <>
      <ModalERP
        isOpen={isOpen}
        onClose={handleClose}
        title="Emitir nota fiscal"
        subtitle={`Venda #${saleNumber} · Cupom → documento fiscal`}
        icon={<FileText size={22} />}
        size="lg"
        loading={loading || loadingSale}
        preventCloseWhileLoading
        footer={
          success ? (
            <button type="button" className="modal-erp-btn-primary" onClick={handleClose}>
              Fechar
            </button>
          ) : (
            <>
              <button
                type="button"
                className="modal-erp-btn-secondary"
                onClick={handleClose}
                disabled={loading}
              >
                Cancelar
              </button>
              {docTipo === DOC_TIPO.NFE && (
                <button
                  type="button"
                  className="modal-erp-btn-secondary"
                  onClick={handleOpenPreview}
                  disabled={loading || loadingSale}
                >
                  Pré-visualizar
                </button>
              )}
              <button
                type="button"
                className="modal-erp-btn-primary"
                onClick={docTipo === DOC_TIPO.NFCE ? handleEmitNfce : handleOpenPreview}
                disabled={loading || loadingSale}
              >
                {loading && <Loader2 size={16} className="modal-erp-spinner" />}
                {docTipo === DOC_TIPO.NFCE ? 'Transmitir NFC-e' : 'Revisar e transmitir NF-e'}
              </button>
            </>
          )
        }
      >
        {success && (
          <div className="emitir-nota-success">
            <CheckCircle size={18} />
            <span>{success.message}</span>
          </div>
        )}

        {error && !success && (
          <div className="emitir-nota-error">
            <AlertCircle size={18} />
            <span>{error}</span>
          </div>
        )}

        {!success && (
          <>
            <div className="emitir-nota-resumo">
              <div className="emitir-nota-resumo-row">
                <span>Cliente atual</span>
                <span>{venda?.customer_name || saleDetail?.customer_name || 'Não informado'}</span>
              </div>
              <div className="emitir-nota-resumo-row">
                <span>Total</span>
                <span>{formatCurrency(totalAmount)}</span>
              </div>
            </div>

            <p className="emitir-nota-hint" style={{ marginBottom: '1rem' }}>
              A emissão não altera o estoque — a baixa já foi registrada na finalização da venda.
            </p>

            <div className="emitir-nota-tipo-tabs">
              <button
                type="button"
                className={`emitir-nota-tipo-tab ${docTipo === DOC_TIPO.NFCE ? 'active' : ''}`}
                onClick={() => setDocTipo(DOC_TIPO.NFCE)}
                disabled={loading}
              >
                <strong>NFC-e</strong>
                <span>Modelo 65 · consumidor final</span>
              </button>
              <button
                type="button"
                className={`emitir-nota-tipo-tab ${docTipo === DOC_TIPO.NFE ? 'active' : ''}`}
                onClick={() => setDocTipo(DOC_TIPO.NFE)}
                disabled={loading}
              >
                <strong>NF-e</strong>
                <span>Modelo 55 · cliente com CPF/CNPJ</span>
              </button>
            </div>

            {docTipo === DOC_TIPO.NFCE && (
              <div className="emitir-nota-field">
                <label htmlFor="emitir-nota-cpf">CPF do consumidor</label>
                <input
                  id="emitir-nota-cpf"
                  type="text"
                  inputMode="numeric"
                  placeholder="000.000.000-00"
                  value={cpf}
                  onChange={(e) => {
                    setCpf(formatCPF(e.target.value))
                    setCpfError('')
                  }}
                  maxLength={14}
                  disabled={loading}
                />
                {cpfError && <p className="emitir-nota-field-error">{cpfError}</p>}
              </div>
            )}

            {docTipo === DOC_TIPO.NFE && (
              <ClienteSearchAutocomplete
                selectedCustomer={selectedCustomer}
                onSelect={setSelectedCustomer}
                onClear={() => setSelectedCustomer(null)}
                disabled={loading}
                onRequestQuickCreate={() => setQuickCreateOpen(true)}
              />
            )}
          </>
        )}
      </ModalERP>

      <CadastroRapidoClienteModal
        isOpen={quickCreateOpen}
        onClose={() => setQuickCreateOpen(false)}
        onSuccess={(customer) => setSelectedCustomer(customer)}
      />

      <NFePreviewModal
        isOpen={previewOpen}
        onClose={() => setPreviewOpen(false)}
        saleId={venda?.id}
        customerId={selectedCustomer?.id}
        payments={nfePayments}
        onConfirm={handleEmitNfe}
        confirmLabel="Confirmar e transmitir NF-e"
        confirming={loading}
        layer={1}
      />
    </>
  )
}

export default EmitirNotaFiscalModal
